import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_lklog-partner/artifacts/wr2fd9vu_Screenshot%202026-04-21%20at%2018-27-46%20f4840f6c-460f-436d-a901-b056ce3790cf.png%20%28PNG%20Image%201536%20%C3%97%201024%20pixels%29.png";

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  const navItems = [
    { to: "/o-nas", label: t("nav.about"), id: "about" },
    { to: "/uslugi", label: t("nav.services"), id: "services" },
    { to: "/branze", label: t("nav.industries"), id: "industries" },
    { to: "/adr", label: t("nav.adr"), id: "adr" },
    { to: "/solutio", label: t("nav.solutio"), id: "solutio" },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0A0A]/90 border-b border-white/10"
          : "bg-[#0A0A0A]/40 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
            <img
              src={LOGO_URL}
              alt="LK LOG"
              className="w-11 h-11 rounded-sm object-contain border border-[#F2B705]/30"
            />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-display text-xl font-bold tracking-tight">
                LK <span className="text-[#F2B705]">LOG</span>
              </span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-neutral-400 mt-1">
                Transport &amp; Spedycja
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {navItems.map((it) => (
              <NavLink
                key={it.id}
                to={it.to}
                data-testid={`nav-link-${it.id}`}
                className={({ isActive }) =>
                  `text-sm transition-colors ${
                    isActive ? "text-[#F2B705]" : "text-neutral-300 hover:text-[#F2B705]"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
            <NavLink
              to="/kontakt"
              data-testid="nav-link-contact"
              className={({ isActive }) =>
                `text-sm transition-colors ${
                  isActive ? "text-[#F2B705]" : "text-neutral-300 hover:text-[#F2B705]"
                }`
              }
            >
              {t("nav.contact")}
            </NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="lang-toggle-btn"
              onClick={toggle}
              className="hidden sm:flex items-center gap-1 text-xs font-semibold tracking-widest text-neutral-400 hover:text-[#F2B705] transition-colors border border-white/10 rounded-sm px-3 py-2"
              aria-label="Toggle language"
            >
              <span className={lang === "pl" ? "text-[#F2B705]" : ""}>PL</span>
              <span className="text-neutral-600">/</span>
              <span className={lang === "en" ? "text-[#F2B705]" : ""}>EN</span>
            </button>

            <Link to="/kontakt" className="hidden md:inline-flex" data-testid="nav-cta-quote">
              <Button className="bg-[#F2B705] hover:bg-[#FFC933] text-[#0A0A0A] font-semibold rounded-sm">
                {t("nav.cta")}
              </Button>
            </Link>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 text-white"
              aria-label="Open menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden border-t border-white/10 bg-[#0A0A0A]/98 backdrop-blur-xl"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {navItems.map((it) => (
              <NavLink
                key={it.id}
                to={it.to}
                data-testid={`mobile-nav-link-${it.id}`}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-[#F2B705]" : "text-neutral-200 hover:text-[#F2B705]"}`
                }
              >
                {it.label}
              </NavLink>
            ))}
            <NavLink
              to="/kontakt"
              data-testid="mobile-nav-link-contact"
              className={({ isActive }) =>
                `text-base ${isActive ? "text-[#F2B705]" : "text-neutral-200 hover:text-[#F2B705]"}`
              }
            >
              {t("nav.contact")}
            </NavLink>
            <button
              data-testid="mobile-lang-toggle"
              onClick={toggle}
              className="self-start text-xs font-semibold tracking-widest text-neutral-300 border border-white/10 rounded-sm px-3 py-2"
            >
              <span className={lang === "pl" ? "text-[#F2B705]" : ""}>PL</span>
              <span className="text-neutral-600 mx-1">/</span>
              <span className={lang === "en" ? "text-[#F2B705]" : ""}>EN</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
